import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    Output,
} from '@angular/core';
import { Customers } from 'county-api';
import { PersonOccupationStatus } from 'county-api/enums';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { debounceTime, map, switchMap, takeUntil } from 'rxjs/operators';
import { JobsService } from '../../jobs/jobs.service';
import moment from 'moment';
import { DateService } from 'src/app/universal/forms/text-datepicker/date.service';

/**
 * The add details person component.
 */
@Component({
    selector: 'person-add-details',
    templateUrl: './person-add-details.component.html',
})
export class PersonAddDetailsComponent implements OnDestroy {

    /**
     * The person data, input from the parent.
     */
    @Input()
    person: Customers.IPerson;

    /**
     * Determines whether the employee is defined, input from the parent.
     */
    @Input()
    employee: boolean;

    /**
     * Allow the disabling of the users name.
     */
    @Input()
    enableNameEditing: boolean;

    @Input()
    onSubmitTriggered = false;

    /**
     * onchange callback, fires the method back to the parent when changed.
     */
    @Output()
    personChange: EventEmitter<any> = new EventEmitter();

    @Output()
    personValidityChange = new EventEmitter<boolean>();

    /**
     * Occupations enum used within the template.
     */
    occupationStatuses = PersonOccupationStatus;

    /**
     * Subject used to track changes to the occupation, this allows for a
     * debounce search.
     */
    searchTerm$ = new BehaviorSubject<string>('');

    /**
     * Observable clean up.
     */
    unsubscribe$ = new Subject();

    /**
     * Observable that has the latest set of jobs to use in suggestions.
     */
    filteredJobs: Observable<Customers.ICustomerJob[]>;

    /**
     * Contructs injected angular dependencies ans set up filtered job
     * observable.
     * @param jobsService The jobs service, to get the filtered jobs for the
     * occupation input.
     */
    constructor(private jobsService: JobsService,
        private dateService: DateService
    ) {
        this.filteredJobs = this.searchTerm$.pipe(
            debounceTime(500),
            switchMap(term => {
                return this.jobsService.get({
                    page: 1,
                    perpage: 10,
                    filter: term,
                });
            }),
            map(response => {
                return response.result.jobs;
            }),
            takeUntil(this.unsubscribe$),
        );
    }

    /**
     * Call back to add the latest occupation status to the subject.
     */
    updateOccupation(term: string) {
        this.searchTerm$.next(term);
    }

    /**
     * Passes the input value to the parent when changed.
     */
    onNameInput() {
        this.personChange.emit();
    }

    onDateChanged(date: string){
        this.person.date_of_birth = date;
    }

    /**
     * Clean up the component.
     */
    ngOnDestroy() {
        this.unsubscribe$.next(null);
        this.unsubscribe$.complete();
    }

    get maxBirthDateMoment(){
        return moment().subtract(16, 'years');
    }

   get minimumBirthDateYear(): number{
        return moment().subtract(100, 'years').year();
    }

    get maxBirthDate(){
        return this.maxBirthDateMoment.format("DD/MM/YYYY");
    }

    isDateStringValid = (dateString: string) => {
        const parsingResult = this.dateService.parseHumanEnteredDate(dateString);
        const formatIsValid = parsingResult.isValid;
        if (!formatIsValid){
            return false;
        }

        const valueIsValid = this.isDateLowerThanMaxDate(parsingResult.value, this.maxBirthDateMoment.toDate())
        && parsingResult.value.getFullYear() >= this.minimumBirthDateYear;

        return formatIsValid && valueIsValid;
    }

    isDateLowerThanMaxDate(date: Date, maxDate: Date){
        return date < maxDate;
    }

    onDobValidityChanged(isValid: boolean){
        this.personValidityChange.emit(isValid);
    }
}
